// Sistema de diseño OTRIX — "Spotify monocromo + acento dorado".
// El tema soporta DOS modos: 'dark' (por defecto) y 'light'. Para no tocar los ~255
// componentes que ya consumen tokens (`gold/ink/hairline/surface/gray*/scheduled/...`),
// se conservan TODOS los nombres de token; solo cambian los VALORES según el modo.
//
// Modo claro = monocromo claro + dorado (ÚNICO acento) + tinta navy. NO se introduce
// azul como segundo acento: el navy es solo tinta de texto/estructura. Se conserva la
// capa premium (sombras teñidas, 1 acento, hairlines, watermark).

// --- Acento dorado: idéntico en ambos modos (la marca no cambia) ---
const gold = {
  main: '#F9BD4E',
  dark: '#F8B131',
  bright: '#F9BD4E', // gold brillante para acentos sobre superficies OSCURAS (hero) en ambos modos
  soft: 'rgba(249, 189, 78, 0.12)',
  gradient: 'linear-gradient(135deg, #F9BD4E 0%, #F8B131 100%)'
};

// En modo CLARO el dorado brillante NO contrasta sobre blanco. Se usa un dorado PROFUNDO
// para texto/íconos/acentos (legible sobre blanco); `bright` queda para superficies oscuras.
const goldLight = {
  ...gold,
  main: '#9A6B00',
  dark: '#7D5700',
  soft: 'rgba(154, 107, 0, 0.14)'
};

// --- Navy: acento secundario SUTIL (referencia MUI). El dorado sigue siendo el acento
// principal; el navy solo aporta profundidad/estructura (hero, estados seleccionados).
// Idéntico en ambos modos. NO reemplaza la regla de "un solo acento".
const navy = {
  main: '#1B2A4A',
  light: '#24365C',
  dark: '#0E1A30',
  soft: 'rgba(27, 42, 74, 0.10)',
  contrastText: '#FFFFFF',
  heroGradient: 'linear-gradient(135deg, #16263F 0%, #0C1626 100%)'
};

// Tokens estructurales por modo.
const dark = {
  // Tinta (off-black, nunca #000 puro) para superficies de CTA y badges.
  ink: { main: '#111418', contrastText: '#F9BD4E' },
  // CTA principal: blanco con texto casi-negro (estilo Spotify).
  primaryCta: { bg: '#FFFFFF', text: '#0A0A0A' },
  // Líneas finas (divisores, bordes de cards) tintadas en blanco a baja opacidad.
  hairline: { main: 'rgba(255, 255, 255, 0.06)', strong: 'rgba(255, 255, 255, 0.12)' },
  // Escala de superficies off-black para profundidad (app < sidebar < content < raised).
  surface: {
    app: '#0E1012',
    sidebar: '#14171A',
    content: '#1F2327',
    raised: '#242A30',
    overlay: 'rgba(255, 255, 255, 0.04)',
    // hover de cards/filas (antes hardcodeado #282828 por toda la app).
    hover: '#282828'
  },
  // Estado seleccionado NEUTRO (menús, tabs, cards activas): gris sobre negro, sin dorado.
  // El dorado queda reservado para microacentos (links, foco, badges, toggle).
  selected: { bg: '#282828', fg: '#FFFFFF' },
  // Fondo de las pantallas de auth: gradiente off-black (nunca #000), sin foto.
  authBackdrop: 'radial-gradient(1100px 700px at 75% -10%, #1F2327 0%, #0E1012 55%)',
  // Texto.
  text: { primary: '#FFFFFF', secondary: 'rgba(255, 255, 255, 0.70)', disabled: 'rgba(255, 255, 255, 0.40)' },
  background: { default: '#0E1012', paper: '#1F2327' },
  // Grises usados por tablas/sticky cells.
  gray0: { main: '#292E33', contrastText: '#FFFFFF' },
  gray1: { main: '#212529', contrastText: '#FFFFFF' },
  gray2: { main: '#1F2327', contrastText: '#FFFFFF' },
  gray3: { main: '#16191C', contrastText: '#FFFFFF' },
  gray4: { main: '#181819', contrastText: '#FFFFFF' },
  divider: '#33393F',
  // Scrollbar slim.
  scrollbar: { thumb: 'rgba(255, 255, 255, 0.14)', thumbHover: 'rgba(255, 255, 255, 0.26)' },
  // Borde grueso entre filas apiladas (mobile responsive 'vertical').
  stackedRowBorder: 'rgba(255, 255, 255, 0.30)',
  // Marca: en oscuro el logo va invertido a blanco; gradiente del hero. El hero es una
  // banda OSCURA con titular blanco y eyebrow dorado en AMBOS modos (tokens hero*).
  brand: {
    logoFilter: 'brightness(0) invert(1)',
    heroGradient: navy.heroGradient,
    heroText: '#FFFFFF',
    heroSubtext: 'rgba(255, 255, 255, 0.62)',
    heroLogoFilter: 'brightness(0) invert(1)',
    heroBorder: 'rgba(255, 255, 255, 0.10)'
  },
  // Sombras teñidas (cards/menus/alerts).
  shadow: {
    menu: 'inset 0 1px 0 rgba(255, 255, 255, 0.04), 0 12px 32px rgba(0, 0, 0, 0.4)',
    alert: '0 8px 24px rgba(0, 0, 0, 0.32)',
    card: '0 8px 24px rgba(0, 0, 0, 0.32)'
  },
  // Realce interior (double-bezel de PremiumCard).
  innerHighlight: 'rgba(255, 255, 255, 0.06)',
  // Tinte para overlays sobre fondos.
  scrim: 'rgba(0, 0, 0, 0.6)',
  // Chips de estado de sesión: tintes suaves + texto legible.
  scheduled: { main: 'rgba(249, 189, 78, 0.16)', contrastText: '#F9BD4E' },
  finished: { main: 'rgba(76, 175, 110, 0.16)', contrastText: '#7BD8A0' },
  absent: { main: 'rgba(255, 255, 255, 0.08)', contrastText: '#C9CDD2' },
  // --- EXCEPCIÓN SEMÁNTICA: color SOLO para las vistas de sesión (semáforo de estado +
  // proximidad). No se usa fuera de components/session/**. Decisión del usuario: los
  // mockups usan azul/amarillo/verde/rojo como indicadores funcionales de la sesión.
  sessionStatus: {
    scheduled: { bg: 'rgba(45, 122, 255, 0.16)', fg: '#7AAEFF' },
    completed: { bg: 'rgba(46, 212, 122, 0.16)', fg: '#5FE0A0' },
    absent: { bg: 'rgba(229, 72, 77, 0.16)', fg: '#FF8A8E' },
    pending: { bg: 'rgba(245, 166, 35, 0.16)', fg: '#F5C451' }
  },
  // Línea de borde izquierdo (3-4px) por proximidad de la fecha.
  sessionProximity: { far: '#3D8BFF', medium: '#F5A623', near: '#2ED47A' },
  // Estado de membresía del cliente (activo / en mora / inactivo). Tokens PROPIOS y no los de
  // `sessionStatus`, que están acotados a components/session/**: aquí el semáforo significa otra
  // cosa (cobro) aunque los tonos coincidan, y mezclarlos ataría dos pantallas sin relación.
  membershipStatus: {
    active: { bg: 'rgba(46, 212, 122, 0.16)', fg: '#5FE0A0' },
    inArrears: { bg: 'rgba(245, 166, 35, 0.16)', fg: '#F5C451' },
    inactive: { bg: 'rgba(229, 72, 77, 0.16)', fg: '#FF8A8E' }
  },
  // Rojo de acción destructiva (íconos/botones de borrar) FUERA de modales. Tono suave, estándar
  // de la app (igual al de sesiones grupales). Dentro de modales se usa el rojo fuerte #B21616.
  deleteAction: { bg: 'rgba(229, 72, 77, 0.16)', fg: '#FF8A8E' },
  // Badge de tendencia de los tiles de estadística (sube/baja/igual). Color SEMÁNTICO, no un
  // segundo acento de marca: sólo tiñe la flecha y el %, nunca una superficie.
  // `success.main`/`error.main` son tonos sólidos para botones y no contrastan sobre off-black.
  trend: { up: '#2ED47A', down: '#FF8A8E', flat: '#C9CDD2' },
  // Rellenos de los iconos animados de los toasts (referencia react-hot-toast). Idénticos en
  // ambos modos: son iconos con trazo blanco sobre su propio círculo, no texto sobre superficie.
  toast: { success: '#61D345', error: '#FF4B4B' }
};

const light = {
  // En claro, negro NEUTRO limpio (principio de las plantillas de correo: botón/texto
  // negro sobre blanco). El CTA principal es ese negro con texto blanco.
  ink: { main: '#1A1A1A', contrastText: '#F9BD4E' },
  primaryCta: { bg: '#1A1A1A', text: '#FFFFFF' },
  // Hairlines NÍTIDAS (no casi-invisibles): así las cards blancas tienen un borde real
  // y dejan de verse como una sola hoja plana.
  hairline: { main: 'rgba(17, 21, 27, 0.12)', strong: 'rgba(17, 21, 27, 0.20)' },
  surface: {
    // Escala espejo de la del modo oscuro: la MISMA jerarquía de tres golpes que se ve en
    // dark (fondo → contenedor más claro → elementos dentro), pero ascendente hacia el
    // blanco. Los saltos son de ~20 puntos porque en el rango claro un salto de 8-10 (lo
    // que había: F6F8FB vs FFFFFF) es literalmente imperceptible y todo se leía plano.
    //   app     #D8DDE6  fondo de la app (gris marcado)
    //   content #EDF0F6  contenedores/secciones (la "segunda capa")
    //   raised  #FFFFFF  cards elevadas
    // El sidebar flota, así que va en blanco: máximo contraste contra el fondo gris.
    app: '#D8DDE6',
    sidebar: '#FFFFFF',
    content: '#EDF0F6',
    raised: '#FFFFFF',
    overlay: 'rgba(17, 21, 27, 0.05)',
    hover: '#E3E8F0'
  },
  // Estado seleccionado NEUTRO en claro: gris sobre blanco (ver nota en `dark`). El gris tiene
  // que ser REAL: con `#EEF0F3` sobre el panel blanco del sidebar el ítem activo desaparecía
  // (contraste ≈1.03) y los desplegables se leían "en blanco".
  selected: { bg: '#DCE2EB', fg: '#11151B' },
  // Fondo de auth en claro: gradiente gris claro equivalente al off-black.
  authBackdrop: 'radial-gradient(1100px 700px at 75% -10%, #FFFFFF 0%, #D8DDE6 55%)',
  text: { primary: '#1A1A1A', secondary: '#454C59', disabled: 'rgba(26, 26, 26, 0.45)' },
  background: { default: '#D8DDE6', paper: '#FFFFFF' },
  // Grises claros para cabeceras/sticky de tablas.
  gray0: { main: '#EDEFF2', contrastText: '#11151B' },
  gray1: { main: '#F4F5F7', contrastText: '#11151B' },
  gray2: { main: '#F0F1F4', contrastText: '#11151B' },
  gray3: { main: '#E4E7EB', contrastText: '#11151B' },
  gray4: { main: '#FAFBFC', contrastText: '#11151B' },
  divider: '#E4E7EB',
  scrollbar: { thumb: 'rgba(17, 21, 27, 0.18)', thumbHover: 'rgba(17, 21, 27, 0.32)' },
  stackedRowBorder: 'rgba(17, 21, 27, 0.16)',
  brand: {
    // Logos normales en claro van a color (navy+dorado): sin filtro.
    logoFilter: 'none',
    // El hero se mantiene como banda OSCURA premium también en claro (foco dramático con
    // acento dorado) — evita el panel casi-blanco plano. Titular/watermark blancos.
    heroGradient: navy.heroGradient,
    heroText: '#FFFFFF',
    heroSubtext: 'rgba(255, 255, 255, 0.62)',
    heroLogoFilter: 'brightness(0) invert(1)',
    heroBorder: 'rgba(255, 255, 255, 0.10)'
  },
  // Sombras en CAPAS (contacto + difusa) para que las cards "leviten" sobre el gris claro.
  shadow: {
    menu: 'inset 0 1px 0 rgba(255, 255, 255, 0.6), 0 12px 32px rgba(17, 21, 27, 0.14)',
    alert: '0 1px 2px rgba(17, 21, 27, 0.05), 0 10px 28px rgba(17, 21, 27, 0.10)',
    card: '0 1px 2px rgba(17, 21, 27, 0.05), 0 10px 28px rgba(17, 21, 27, 0.10)'
  },
  // En claro el realce blanco es invisible sobre superficies blancas: el "bezel" pasa a
  // ser una arista superior OSCURA sutil (mismo rol visual que el highlight en oscuro).
  innerHighlight: 'rgba(17, 21, 27, 0.06)',
  scrim: 'rgba(17, 21, 27, 0.45)',
  // En claro el tinte dorado sobre blanco necesita texto más oscuro para AA.
  scheduled: { main: 'rgba(249, 189, 78, 0.20)', contrastText: '#9A6B00' },
  finished: { main: 'rgba(27, 147, 75, 0.14)', contrastText: '#1B7A43' },
  absent: { main: 'rgba(17, 21, 27, 0.06)', contrastText: '#525866' },
  // --- EXCEPCIÓN SEMÁNTICA: color SOLO para las vistas de sesión (ver nota en `dark`).
  // En claro los textos van más oscuros para AA sobre el tinte claro.
  sessionStatus: {
    scheduled: { bg: 'rgba(10, 101, 255, 0.10)', fg: '#0A55D6' },
    completed: { bg: 'rgba(27, 147, 75, 0.12)', fg: '#157A3C' },
    absent: { bg: 'rgba(178, 22, 22, 0.10)', fg: '#C42121' },
    pending: { bg: 'rgba(194, 116, 10, 0.12)', fg: '#A8650A' }
  },
  // Línea de borde izquierdo (3-4px) por proximidad de la fecha (sólidos legibles sobre blanco).
  sessionProximity: { far: '#2563EB', medium: '#C2740A', near: '#1B934B' },
  // Estado de membresía (ver nota en `dark`): en claro los textos van más oscuros para AA.
  membershipStatus: {
    active: { bg: 'rgba(27, 147, 75, 0.12)', fg: '#157A3C' },
    inArrears: { bg: 'rgba(194, 116, 10, 0.12)', fg: '#A8650A' },
    inactive: { bg: 'rgba(178, 22, 22, 0.10)', fg: '#C42121' }
  },
  // Rojo de acción destructiva fuera de modales (más oscuro para AA sobre blanco).
  deleteAction: { bg: 'rgba(178, 22, 22, 0.10)', fg: '#C42121' },
  // Badge de tendencia (ver nota en `dark`): en claro los tonos van sólidos para AA sobre blanco.
  trend: { up: '#1B7A43', down: '#C42121', flat: '#525866' },
  // Toasts: mismos rellenos que en oscuro (ver nota en `dark`).
  toast: { success: '#61D345', error: '#FF4B4B' }
};

/**
 * Crea la paleta MUI según el modo.
 *
 * @param {'dark'|'light'} mode
 * @returns {object} paleta lista para createTheme
 */
export const createPalette = (mode = 'dark') => {
  const tokens = mode === 'light' ? light : dark;

  return {
    mode,
    primary: {
      dark: '#11178C',
      main: '#171FB9'
    },
    secondary: {
      // Mode-aware: dorado profundo en claro (legible en links/tabs sobre blanco).
      dark: mode === 'light' ? '#7D5700' : '#F8B131',
      main: mode === 'light' ? '#9A6B00' : '#F9BD4E'
    },
    success: {
      dark: '#126332',
      main: '#1B934B'
    },
    info: {
      dark: '#0A65FF',
      main: '#2D7AFF'
    },
    warning: {
      dark: '#E7B140',
      main: '#EBBD5C'
    },
    error: {
      dark: '#841010',
      main: '#B21616'
    },
    menuItem: {
      main: mode === 'light' ? '#525866' : '#BBC2C9',
      // Color del ítem al pasar el mouse / del chevron de los desplegables. En claro
      // tiene que OSCURECERSE (antes era blanco fijo y desaparecía sobre el sidebar).
      hover: mode === 'light' ? '#11151B' : '#FFFFFF',
      contrastText: '#333333'
    },
    muiError: {
      main: '#F6B1B1',
      contrastText: '#333333'
    },
    white: {
      main: '#FFFFFF',
      contrastText: '#333333'
    },
    black: {
      main: '#333333',
      contrastText: '#FFFFFF'
    },
    // backwards-compat: algunos componentes usan clientAbsent/trainerAbsent.
    clientAbsent: tokens.absent,
    trainerAbsent: tokens.absent,
    // --- Sistema premium blanco/negro/dorado (taste-skill: 1 acento = dorado) ---
    // Dorado mode-aware: brillante en oscuro, profundo (legible) en claro.
    gold: mode === 'light' ? goldLight : gold,
    // Navy como token disponible (acento secundario sutil, opt-in por componente).
    navy,
    ...tokens
  };
};

// Backward-compat: el export por defecto es la paleta OSCURA estática. Varios módulos
// (charts, styled components y utils) importan la paleta directamente y leen
// `palette.X.main` en tiempo de import, así que NO pueden reaccionar al modo; siguen
// recibiendo la paleta oscura como antes. El tema mode-aware usa el named `createPalette`.
const palette = createPalette('dark');

export default palette;
